import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Chip } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import { CompactCustomerChip } from "../../../styled/pos/windows11";
import { setShowAddressForm } from "../../../../store/actions/newOrderSlice";
import { formatFinance } from "../../../../utils/formatters";

/**
 * CompactAddressInfo - Compact address display for command bar
 *
 * Empty state: "+ Adicionar Endereço" with AddLocationIcon
 * Filled state: Street + number, delivery fee chip
 * Click: Opens AddressFormModal
 * Only shows when orderType === "DELIVERY"
 */
const CompactAddressInfo = ({ iconOnly }) => {
  const dispatch = useDispatch();
  const address = useSelector((state) => state.newOrder.address);
  const orderType = useSelector((state) => state.newOrder.orderType);

  // Don't show for TAKEOUT orders
  if (orderType !== "DELIVERY") {
    return null;
  }

  const handleClick = () => {
    dispatch(setShowAddressForm(true));
  };

  return (
    <CompactCustomerChip
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        minWidth: iconOnly ? "auto" : (address ? 240 : 180),
        bgcolor: address ? "primary.50" : "background.paper",
        borderColor: address ? "primary.main" : "divider",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: address ? "primary.100" : "action.hover",
        },
      }}
    >
      {address ? (
        iconOnly ? (
          <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
        ) : (
          // Filled state - show address summary
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {address.street}, {address.house_number}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {address.neighborhood}
              </Typography>
            </Box>
            <Chip
              icon={<DeliveryDiningIcon />}
              label={formatFinance(address.fee)}
              size="small"
              color="primary"
              sx={{
                height: 24,
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )
      ) : (
        // Empty state - prompt to add address
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddLocationIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          {!iconOnly && (
            <Typography variant="body2" color="text.secondary">
              + Adicionar Endereço
            </Typography>
          )}
        </Box>
      )}
    </CompactCustomerChip>
  );
};

export default CompactAddressInfo;
